import type { useDisclosure } from "@mantine/hooks";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "~/utils/api";
import { useI18nContext } from "~/i18n/i18n-react";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "../ui/button";

interface RoomDeletionConfirmationModalProps {
	disclosure: ReturnType<typeof useDisclosure>;
	roomId: string;
}

export const RoomDeletionConfirmationModal = ({
	disclosure,
	roomId,
}: RoomDeletionConfirmationModalProps) => {
	const [opened, { close }] = disclosure;
	const { LL } = useI18nContext();
	const utils = api.useUtils();

	const mutation = api.room.end.useMutation({
		onSuccess: () => {
			void utils.room.invalidate();
			close();
		},
	});

	const handleDelete = () => {
		mutation.mutate({ roomId });
	};

	return (
		<AlertDialog open={opened} onOpenChange={close}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-destructive" />
						{LL.DELETE_CONFIRMATION_MODAL.TITLE()}
					</AlertDialogTitle>
					<AlertDialogDescription>
						{LL.DELETE_CONFIRMATION_MODAL.DESCRIPTION()}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={mutation.isLoading}>
						{LL.DELETE_CONFIRMATION_MODAL.CANCEL()}
					</AlertDialogCancel>
					<Button
						onClick={handleDelete}
						disabled={mutation.isLoading}
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
					>
						{mutation.isLoading ? (
							<span className="flex items-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin" />
								{LL.DELETE_CONFIRMATION_MODAL.DELETE()}
							</span>
						) : (
							LL.DELETE_CONFIRMATION_MODAL.DELETE()
						)}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
